import React from "react";
import { RSD } from "../../../lib/api";

// Pure presentation: every number below comes from the backend analytics calculation.
const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const mins = (v) => (v === null || v === undefined ? "—" : `${v} min`);
const NO_DATA = "Nema dovoljno podataka";

export function Card({ label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border p-3" data-testid={testid}>
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
      <p className="font-display font-black text-xl mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between gap-3 p-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-right">{value}{sub && <span className="block text-[11px] font-normal text-slate-400">{sub}</span>}</span>
    </div>
  );
}

function StatBlock({ title, s, testid }) {
  return (
    <div className="bg-white rounded-2xl border divide-y" data-testid={testid}>
      <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">{title}</p>
      {!s || !s.count ? <p className="p-3 text-sm text-slate-400">{NO_DATA}</p> : (
        <>
          <Row label="Prosek" value={mins(s.avg)} />
          <Row label="Medijana" value={mins(s.median)} />
          <Row label="P90" value={mins(s.p90)} />
          <Row label="P95" value={mins(s.p95)} />
          <Row label="Porudžbina u obračunu" value={s.count} />
        </>
      )}
    </div>
  );
}

export function OverviewTab({ d }) {
  const o = d.overview, l = d.lost_orders;
  return (
    <div className="space-y-4" data-testid="oa-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card label="Završene" value={o.completed_orders} testid="oa-completed-orders" />
        <Card label="Promet" value={RSD(o.completed_revenue)} testid="oa-revenue" />
        <Card label="Prosečna korpa" value={RSD(o.avg_order_value)} testid="oa-aov" />
        <Card label="Artikala / porudžbini" value={o.items_per_order} testid="oa-items-per-order" />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Izgubljena vrednost (nije promet)</p>
        <div className="bg-white rounded-2xl border divide-y" data-testid="oa-lost">
          <Row label="Otkazane" value={`${l.CANCELLED.orders} · ${RSD(l.CANCELLED.lost_order_value)}`} sub={pct(l.CANCELLED.share_of_created) + " kreiranih"} />
          <Row label="Odbijene" value={`${l.REJECTED.orders} · ${RSD(l.REJECTED.lost_order_value)}`} sub={pct(l.REJECTED.share_of_created) + " kreiranih"} />
          <Row label="Istekle" value={`${l.EXPIRED.orders} · ${RSD(l.EXPIRED.lost_order_value)}`} sub={pct(l.EXPIRED.share_of_created) + " kreiranih"} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {["PONESI", "VISTA", "LEGACY_UNKNOWN"].map((s) => (
          <Card key={s} testid={`oa-source-${s}`} label={s === "LEGACY_UNKNOWN" ? "Nepoznat izvor" : s}
            value={d.by_source[s].completed_orders} sub={RSD(d.by_source[s].completed_revenue)} />
        ))}
      </div>
    </div>
  );
}

export function PaymentTab({ d }) {
  const p = d.by_payment;
  return (
    <div className="space-y-2" data-testid="oa-payment">
      {["CASH", "CARD", "UNKNOWN"].map((pm) => (
        (pm !== "UNKNOWN" || p.UNKNOWN.completed_orders > 0) && (
          <div key={pm} className="bg-white rounded-2xl border divide-y" data-testid={`oa-pay-${pm}`}>
            <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">
              {pm === "CASH" ? "Gotovina" : pm === "CARD" ? "Kartica" : "Nepoznato (stari podaci)"}
            </p>
            <Row label="Završene porudžbine" value={p[pm].completed_orders} />
            <Row label="Promet" value={RSD(p[pm].completed_revenue)} />
            <Row label="Udeo po broju" value={pct(p[pm].order_share)} />
            <Row label="Udeo po prometu" value={pct(p[pm].revenue_share)} />
          </div>
        )
      ))}
    </div>
  );
}

export function TimingTab({ d }) {
  const t = d.timings;
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="oa-timings">
      <StatBlock title="Odgovor restorana (kreirano → prihvaćeno)" s={t.restaurant_response_minutes} testid="oa-t-response" />
      <StatBlock title="Priprema (prihvaćeno → spremno)" s={t.preparation_minutes} testid="oa-t-prep" />
      <StatBlock title="Čekanje na preuzimanje (spremno → preuzeto)" s={t.pickup_wait_minutes} testid="oa-t-wait" />
      <StatBlock title="Ukupan ciklus (kreirano → preuzeto)" s={t.total_lifecycle_minutes} testid="oa-t-life" />
    </div>
  );
}

export function LatenessTab({ d }) {
  const l = d.lateness;
  return (
    <div className="space-y-2" data-testid="oa-lateness">
      <div className="grid grid-cols-2 gap-2">
        <Card label="Na vreme" value={pct(l.on_time_percentage)} sub={`${l.on_time_orders} porudžbina`} testid="oa-on-time" />
        <Card label="Kasno" value={pct(l.late_percentage)} sub={`${l.late_orders} porudžbina`} testid="oa-late" />
      </div>
      <StatBlock title="Kašnjenje (spremno vs dogovoreno vreme)" s={l.lateness_minutes} testid="oa-late-stats" />
      <p className="text-[11px] text-slate-400">
        Kašnjenje se meri kao SPREMNO vs potvrđeno vreme preuzimanja. Čekanje kupca posle SPREMNO je
        odvojena metrika. Izuzeto zbog nepotpunih vremena: {l.excluded_missing_timestamps}.
      </p>
    </div>
  );
}

export function PickupTab({ d }) {
  const p = d.pickup, c = p.cumulative;
  return (
    <div className="space-y-2" data-testid="oa-pickup">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["≤ 5 min", c.within_5_min], ["≤ 10 min", c.within_10_min], ["≤ 15 min", c.within_15_min],
          ["≤ 30 min", c.within_30_min], ["30+ min", c.over_30_min]].map(([l, v]) => (
          <Card key={l} label={l} value={pct(v)} testid={`oa-pickup-${l.replace(/[^0-9+]/g, "") || "x"}`} />
        ))}
      </div>
      <StatBlock title="Čekanje posle SPREMNO" s={p.wait_minutes} testid="oa-pickup-stats" />
      <div className="bg-white rounded-2xl border divide-y">
        <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">Raspodela</p>
        {Object.entries(p.distribution).map(([k, v]) => (
          <Row key={k} label={k.replace("_plus", "+ min").replace("_", "–")} value={v} />
        ))}
      </div>
    </div>
  );
}

export function DiscountTab({ d }) {
  const x = d.discounts;
  const labels = { HAPPY_HOUR: "Happy Hour", STORE_WIDE: "Popust na sve", PRODUCT_DISCOUNT: "Popust na proizvod", OTHER: "Ostalo" };
  const any = Object.values(x.by_type).some((v) => v.completed_orders > 0);
  return (
    <div className="space-y-2" data-testid="oa-discounts">
      <div className="grid grid-cols-3 gap-2">
        <Card label="Bruto" value={RSD(x.gross_value)} testid="oa-disc-gross" />
        <Card label="Dat popust" value={RSD(x.discount_given)} testid="oa-disc-given" />
        <Card label="Neto" value={RSD(x.net_revenue)} testid="oa-disc-net" />
      </div>
      {!any ? <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400">{NO_DATA}</p> :
        Object.entries(x.by_type).filter(([, v]) => v.completed_orders > 0).map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl border divide-y" data-testid={`oa-disc-${k}`}>
            <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">{labels[k]}</p>
            <Row label="Završene porudžbine" value={v.completed_orders} />
            <Row label="Prodatih komada" value={v.units} />
            <Row label="Bruto → neto" value={`${RSD(v.gross_value)} → ${RSD(v.net_revenue)}`} sub={`popust ${RSD(v.discount_given)}`} />
            <Row label="Prosečan popust" value={v.avg_discount_percent === null ? "—" : `${v.avg_discount_percent}%`} />
          </div>
        ))}
    </div>
  );
}

export function FunnelTab({ d }) {
  const f = d.funnel, a = d.asap_restriction;
  const labels = {
    RESTAURANT_CARD_IMPRESSION: "Prikaz kartice", RESTAURANT_VIEW: "Pregled restorana",
    ADD_TO_CART: "Dodato u korpu", CHECKOUT: "Započet checkout",
    ORDER_CREATED: "Porudžbina kreirana", COMPLETED: "Preuzeto",
  };
  return (
    <div className="space-y-3" data-testid="oa-funnel">
      <p className="text-[11px] text-slate-400">
        Online (Ponesi) funnel — Vista je namerno izuzeta jer nema kupčevu web sesiju.
        Bihevioralni koraci se mere u jedinstvenim sesijama, od uvođenja first-party analitike.
      </p>
      <div className="bg-white rounded-2xl border divide-y">
        {f.stages.map((s) => (
          <div key={s.stage} className="p-3 text-sm" data-testid={`oa-funnel-${s.stage}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-bold">{labels[s.stage]}</span>
              <span className="font-display font-black">{s.sessions}</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Konverzija koraka: {pct(s.stage_conversion)} · Odustajanje: {s.drop_off ?? "—"} ({pct(s.drop_off_rate)}) · Ukupno: {pct(s.overall_conversion)}
            </p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border divide-y" data-testid="oa-funnel-business">
        <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">Poslovne porudžbine (Ponesi)</p>
        <Row label="Kreirano" value={f.business_orders.orders_created} />
        <Row label="Preuzeto" value={f.business_orders.orders_completed} />
        <Row label="Sa vezom na sesiju" value={f.business_orders.orders_with_session_attribution} />
      </div>
      <div className="bg-white rounded-2xl border divide-y" data-testid="oa-asap">
        <p className="p-3 text-xs font-black uppercase tracking-wider text-slate-400">ASAP ograničenje &gt; 3.000 RSD</p>
        {!a.affected_sessions ? <p className="p-3 text-sm text-slate-400">{NO_DATA}</p> : (
          <>
            <Row label="Pogođene sesije" value={a.affected_sessions} />
            <Row label="Prosečna korpa" value={RSD(a.avg_cart_value || 0)} sub={`medijana ${RSD(a.median_cart_value || 0)}`} />
            <Row label="Izabrano zakazano preuzimanje" value={pct(a.scheduled_pickup_selected_rate)} />
            <Row label="Kreirana porudžbina" value={pct(a.order_created_rate)} />
            <Row label="Preuzeta porudžbina" value={pct(a.order_completed_rate)} />
            <Row label="Checkout nije završen" value={pct(a.checkout_not_completed_rate)} />
          </>
        )}
        <p className="p-3 text-[11px] text-slate-400">Korelacija, ne dokaz uzročnosti.</p>
      </div>
    </div>
  );
}
